// Only participating writers in the same origin/storage bucket are ordered.
// No credential, identity, timeout or automatic uncertainty-marker retirement.
export const CREDENTIAL_LOCK = "caltana:cw-browser-credential-write:v1";
export const CREDENTIAL_PENDING = "caltana:cw-browser-credential-write-pending:v1";

export class CredentialOrderUnavailable extends Error {
  readonly status = 503;
  constructor() {
    super(
      "A session operation could not be confirmed. Use a separate browser context to continue safely.",
    );
  }
}

// Only the request adapter emits this after fetch/body completion is unknown.
export class CredentialResponseUncertain extends CredentialOrderUnavailable {}

export const browserCredentialOrder = {
  available: (): boolean =>
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.locks?.request === "function",
  async run<T>(operation: () => Promise<T>): Promise<T> {
    if (!browserCredentialOrder.available()) throw new CredentialOrderUnavailable();
    // No abort, steal or timeout: a discarded UI does not prove its response
    // cannot still write cookies. The lock spans CSRF, final response and proof.
    return navigator.locks.request(CREDENTIAL_LOCK, { mode: "exclusive" }, async () => {
      let marker: string;
      try {
        if (localStorage.getItem(CREDENTIAL_PENDING) !== null)
          throw new CredentialOrderUnavailable();
        marker = crypto.randomUUID();
        localStorage.setItem(CREDENTIAL_PENDING, marker);
        if (localStorage.getItem(CREDENTIAL_PENDING) !== marker)
          throw new CredentialOrderUnavailable();
      } catch {
        throw new CredentialOrderUnavailable();
      }
      // Destruction releases Web Locks, but the marker survives. A rejected or
      // undrained fetch remains uncertain; a fully drained protocol denial may
      // release normally so ordinary failures do not permanently block retry.
      let uncertain = false;
      try {
        return await operation();
      } catch (error) {
        uncertain = error instanceof CredentialResponseUncertain;
        throw error;
      } finally {
        if (!uncertain) {
          try {
            if (localStorage.getItem(CREDENTIAL_PENDING) !== marker)
              throw new CredentialOrderUnavailable();
            localStorage.removeItem(CREDENTIAL_PENDING);
            if (localStorage.getItem(CREDENTIAL_PENDING) !== null)
              throw new CredentialOrderUnavailable();
          } catch {
            throw new CredentialOrderUnavailable();
          }
        }
      }
    });
  },
};
